import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validateEducation } from './validation';
import { MONTHS, YEAR_RANGE, parseDateString } from './constants';

const EducationSection = memo(({ 
    initialData, 
    authAxios, 
    setError, 
    onUpdate 
}) => {
    const [education, setEducation] = useState(initialData || []);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);

    useEffect(() => {
        if (initialData) {
            // Parse dates from API (YYYY-MM-DD) to month/year components
            const parsedData = initialData.map(edu => {
                const startParsed = parseDateString(edu.start_date);
                const endParsed = parseDateString(edu.end_date);
                return {
                    ...edu,
                    start_month: startParsed.month,
                    start_year: startParsed.year,
                    end_month: endParsed.month,
                    end_year: endParsed.year,
                };
            });
            setEducation(parsedData);
        }
    }, [initialData]);

    const addEducation = useCallback(() => {
        setEducation(prev => [...prev, {
            id: Date.now(),
            school: '',
            degree: '',
            major: '',
            gpa: '',
            start_month: '',
            start_year: '',
            end_month: '',
            end_year: '',
        }]);
    }, []);

    const updateEducation = useCallback((id, field, value) => {
        setEducation(prev => prev.map(edu => 
            edu.id === id ? { ...edu, [field]: value } : edu
        ));
    }, []);

    const removeEducation = useCallback((id) => {
        setEducation(prev => prev.filter(edu => edu.id !== id));
    }, []);

    // Format date for API (YYYY-MM-DD)
    const formatDate = (month, year) => {
        if (!month || !year) return null;
        return `${year}-${month.padStart(2, '0')}-01`;
    };

    const saveEducationEntry = async (edu) => {
        try {
            const payload = {
                institution: edu.school || edu.institution,
                degree: edu.degree,
                major: edu.major || '',
                gpa: edu.gpa || null,
                start_date: formatDate(edu.start_month, edu.start_year),
                end_date: formatDate(edu.end_month, edu.end_year),
            };

            if (edu.id && typeof edu.id === 'number' && edu.id > 1000000000000) {
                // New entry - POST
                const response = await authAxios.post('/education/', payload);
                return response.data;
            } else {
                // Existing entry - PATCH
                const response = await authAxios.patch(`/education/${edu.id}/`, payload);
                return response.data;
            }
        } catch (error) {
            console.error('Failed to save education:', error);
            throw error;
        }
    };

    const handleSaveAll = async () => {
        // Validate
        const errors = validateEducation(education);
        if (errors.length > 0) {
            setShowErrors(true);
            const fieldErrors = errors.map(e => {
                const fields = Object.keys(e).filter(k => k !== 'index');
                return `Entry ${e.index + 1}: ${fields.join(', ')}`;
            });
            setError?.(`Education validation failed: ${fieldErrors.join('; ')}`);
            return;
        }


        setSaving(true);

        try {
            const savedEntries = await Promise.all(
                education.map(edu => saveEducationEntry(edu))
            );
            // Parse dates from response
            const parsedEntries = savedEntries.map(edu => {
                const startParsed = parseDateString(edu.start_date);
                const endParsed = parseDateString(edu.end_date);
                return {
                    ...edu,
                    start_month: startParsed.month,
                    start_year: startParsed.year,
                    end_month: endParsed.month,
                    end_year: endParsed.year,
                };
            });
            setEducation(parsedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to save education';
            setError?.(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    // Check if entry has error
    const hasFieldError = (eduIndex, field) => {
        if (!showErrors) return false;
        const edu = education[eduIndex];
        if (!edu) return false;
        
        if (field === 'school' && !(edu.school?.trim() || edu.institution?.trim())) return true;
        if (field === 'degree' && !edu.degree?.trim()) return true;
        if (field === 'start_date' && (!edu.start_month || !edu.start_year)) return true;
        return false;
    };

    // Dropdown styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
    };
    
    const errorSelectStyles = {
        ...selectStyles,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#fca5a5',
            borderWidth: '1.5px',
        },
        backgroundColor: '#fef2f2',
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>
                    Education
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={addEducation}
                        startIcon={<AddIcon />}
                        sx={{
                            color: '#4f46e5',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                        }}
                    >
                        Add Education
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveAll}
                        disabled={saving || education.length === 0}
                        sx={{
                            bgcolor: '#4f46e5',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: '6px',
                            px: 3,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                    </Button>
                </Box>
            </Box>

            {education.length === 0 ? (
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    No education entries. Click "Add Education" to add one.
                </Typography>
            ) : (
                education.map((edu, index) => (
                    <Box key={edu.id} sx={{ 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px', 
                        p: 3, 
                        mb: 2,
                        position: 'relative',
                    }}>
                        <IconButton
                            size="small"
                            onClick={() => removeEducation(edu.id)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    School<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="School name"
                                    value={edu.school || edu.institution || ''}
                                    onChange={(e) => updateEducation(edu.id, 'school', e.target.value)}
                                    sx={hasFieldError(index, 'school') ? getInputStyles(true, true) : inputStyles}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Degree<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Degree (e.g., Bachelor's, Master's)"
                                    value={edu.degree || ''}
                                    onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                                    sx={hasFieldError(index, 'degree') ? getInputStyles(true, true) : inputStyles}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Start Date<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span>
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={edu.start_month || ''}
                                        onChange={(e) => updateEducation(edu.id, 'start_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={hasFieldError(index, 'start_date') ? errorSelectStyles : selectStyles}
                                    >
                                        <MenuItem value="" disabled>Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={edu.start_year || ''}
                                        onChange={(e) => updateEducation(edu.id, 'start_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={hasFieldError(index, 'start_date') ? errorSelectStyles : selectStyles}
                                    >
                                        <MenuItem value="" disabled>Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    End Date
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                    <Select
                                        size="small"
                                        value={edu.end_month || ''}
                                        onChange={(e) => updateEducation(edu.id, 'end_month', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Month</MenuItem>
                                        {MONTHS.map(m => (
                                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                        ))}
                                    </Select>
                                    <Select
                                        size="small"
                                        value={edu.end_year || ''}
                                        onChange={(e) => updateEducation(edu.id, 'end_year', e.target.value)}
                                        displayEmpty
                                        IconComponent={ExpandMoreIcon}
                                        sx={selectStyles}
                                    >
                                        <MenuItem value="">Year</MenuItem>
                                        {YEAR_RANGE.map(y => (
                                            <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    Major / Field of Study
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Major"
                                    value={edu.major || ''}
                                    onChange={(e) => updateEducation(edu.id, 'major', e.target.value)}
                                    sx={inputStyles}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                                    GPA
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="GPA (e.g., 3.8)"
                                    value={edu.gpa || ''}
                                    onChange={(e) => updateEducation(edu.id, 'gpa', e.target.value)}
                                    sx={inputStyles}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))
            )}
        </Box>
    );
});

EducationSection.displayName = 'EducationSection';

export default EducationSection;
